import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceDayRecord} from '@/entities/attendance/types';
import {knownFlags} from '@/features/view-my-attendance/lib/flags';
import {formatDayOfMonth} from '@/shared/lib/format';
import {Card} from '@/shared/ui/card';
import {HelpNote} from '@/shared/ui/explain';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';
import {PersonDay} from './PersonDayList';

/**
 * One person's period as a calendar, not as thirty cards.
 *
 * A month of attendance is a SHAPE — every Tuesday late, the week of the 14th missing,
 * nothing at all after the 22nd — and a vertical list of thirty rows destroys it. You
 * cannot see a pattern you have to scroll through: by the time the reader reaches the
 * 20th they no longer remember what the 6th said. Laid out seven across, the same data
 * answers "how did this month go" before a single row is read, and weekends line up in
 * a column instead of hiding among the workdays.
 *
 * The grid is an INDEX, not the whole story: a cell carries a date and a colour, and
 * picking one opens that day's real card underneath — the same `PersonDay` the list
 * below the calendar uses, expanded, with its in→out episodes and that day's raw
 * journal. So nothing is lost by showing less: the detail moved from "always, thirty
 * times" to "on demand, once" — which is also the only affordable way to show the
 * journal at all, since a day of it costs a walk down the access log (see DayLog).
 *
 * Colour follows the flags exactly as the rest of the app does — green kept the shift,
 * amber something is off, red the day is missing, grey there was no rule to judge it
 * against. A day nobody was expected in stays nearly blank, which is what makes the
 * absences stand out instead of competing with every Sunday.
 */
/**
 * Seven columns, always — so the width of a cell is exactly a seventh and the gaps live
 * INSIDE it as a margin.
 *
 * The obvious spelling (`gap` on the grid, `flexBasis: '13.2%'` on the cell) is a trap:
 * a percentage basis is taken of the container, the gaps are added on top, and the row
 * overflows and wraps to six columns on any window narrower than about 320pt. A
 * calendar that silently becomes a six-day week is worse than no calendar, so the cell
 * takes the full seventh and pads itself.
 */
const COLUMN = `${100 / 7}%`;

const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {flexDirection: 'row'},
  headCell: {width: COLUMN, alignItems: 'center', paddingBottom: spacing(1)},
  grid: {flexDirection: 'row', flexWrap: 'wrap'},
  cell: {width: COLUMN, padding: 2},
  cellInner: {
    minHeight: 40,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  dayNum: {fontFamily: fonts.mono.medium, fontSize: 14, lineHeight: 18},
  legend: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  legendItem: {flexDirection: 'row', alignItems: 'center', gap: spacing(1)},
  swatch: {width: 10, height: 10, borderRadius: 3},
});

type Skin = {bg: string; fg: string};

/**
 * The verdict a cell is painted with. Order matters and is the order a reader cares
 * about: a day that is both late and overtime is a late day.
 */
function skinFor(day: AttendanceDayRecord, p: Palette): Skin {
  const flags = new Set(knownFlags(day.flags));

  // Праздник календаря (SV-54) без отметок — свой цвет: не прогул и не просто
  // выходной. С отметками день красится по вердикту, как обычно.
  if (day.day_kind === 'holiday' && day.event_count === 0) {
    return {bg: p.infoSoft, fg: p.infoInk};
  }
  if (flags.has('absent')) {
    return {bg: p.destructiveSoft, fg: p.destructiveInk};
  }
  if (flags.has('late') || flags.has('earlyLeave') || flags.has('incomplete')) {
    return {bg: p.warningSoft, fg: p.warningInk};
  }
  if (flags.has('onTime')) {
    return {bg: p.signalSoft, fg: p.signalInk};
  }
  if (day.event_count > 0) {
    // Present, but with no schedule to be judged against — a fact, not a verdict.
    return {bg: p.muted, fg: p.foreground};
  }
  // Nobody was expected and nobody came: the quietest thing on the grid.
  return {bg: withAlpha(p.mutedForeground, 0.05), fg: p.mutedForeground};
}

export function MonthCalendar({
  days,
  companyId,
  recordId,
  onOpenArchive,
}: {
  days: AttendanceDayRecord[];
  /** Passed to the picked day's card so it can pull that day's journal. */
  companyId?: string;
  recordId?: string;
  onOpenArchive?: (date: string) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [selected, setSelected] = useState<string | null>(null);

  if (days.length === 0) {
    return null;
  }

  const current = days.find(d => d.date === selected) ?? null;
  // The period rarely starts on a Monday, so the first row is padded to put every date
  // under its own weekday. `weekday` is 1..7 Mon-first, matching sv_control.
  const lead = Math.max(0, (days[0].weekday ?? 1) - 1);

  const legend = (
    [
      {tone: {bg: p.signalSoft, fg: p.signal}, label: t('attendance.flag.onTime')},
      {tone: {bg: p.warningSoft, fg: p.warning}, label: t('reports.legendIssue')},
      {tone: {bg: p.destructiveSoft, fg: p.destructive}, label: t('attendance.summary.absent')},
      {tone: {bg: p.infoSoft, fg: p.info}, label: t('reports.legendHoliday')},
      {
        tone: {bg: withAlpha(p.mutedForeground, 0.1), fg: p.mutedForeground},
        label: t('reports.legendOff'),
      },
    ] as const
  ).map(item => (
    <View key={item.label} style={styles.legendItem}>
      <View style={[styles.swatch, {backgroundColor: item.tone.fg}]} />
      <AppText variant="micro">{item.label}</AppText>
    </View>
  ));

  return (
    <View style={styles.root}>
      <Card padding={4} style={styles.root} testID="month-calendar">
        <View style={styles.head}>
          {[1, 2, 3, 4, 5, 6, 7].map(index => (
            <View key={index} style={styles.headCell}>
              <AppText variant="micro" color={index > 5 ? p.mutedForeground : undefined}>
                {t(`attendance.weekday.${index}`)}
              </AppText>
            </View>
          ))}
        </View>

        <View style={styles.grid}>
          {Array.from({length: lead}, (_, i) => (
            <View key={`lead-${i}`} style={styles.cell} />
          ))}
          {days.map(day => {
            const skin = skinFor(day, p);
            const active = day.date === selected;
            return (
              <Pressable
                key={day.date}
                accessibilityRole="button"
                accessibilityState={{selected: active}}
                accessibilityLabel={day.date}
                onPress={() => setSelected(active ? null : day.date)}
                testID={`calendar-day-${day.date}`}
                style={styles.cell}>
                <View
                  style={[
                    styles.cellInner,
                    {
                      backgroundColor: skin.bg,
                      borderColor: active ? p.primary : 'transparent',
                    },
                  ]}>
                  <AppText style={styles.dayNum} color={skin.fg}>
                    {formatDayOfMonth(day.date)}
                  </AppText>
                </View>
              </Pressable>
            );
          })}
        </View>

        <View style={styles.legend}>{legend}</View>

        <HelpNote text={t('reports.calendarHint')} />
      </Card>

      {/* The picked day, in full — the same card the list below draws, already open. */}
      {current ? (
        <PersonDay
          day={current}
          defaultOpen
          companyId={companyId}
          recordId={recordId}
          onOpenArchive={onOpenArchive}
        />
      ) : null}
    </View>
  );
}
